// Agent-readiness scorecards: one question set per audience, shared scoring.
// Pure data and functions, so the scoring rules can be tested without a browser.
export const MAX_SCORE = 21;
// Replace with a booking link when one exists; the result page falls back to /contact.
export const CALENDAR_URL = "";

const o = (key, points, text) => ({ key, points, text });
const four = (a, b, c, d) => [
  o("A", 3, a),
  o("B", 2, b),
  o("C", 1, c),
  o("D", 0, d),
];

// Every set keeps the same positions for the shared questions, so the scoring
// rules below work on any of them: 3 = AI use, 4 = AI access, 5 = security.
const AI_USE = 3;
const SECURITY = 5;

// The three bands are the same for every audience. Only the advice differs.
export const BANDS = [
  {
    id: "scattered",
    short: "Scattered",
    title: "Scattered: foundations first",
    range: "0–8",
    meaning:
      "Data and process are too fragmented for an agent to help safely yet.",
  },
  {
    id: "ready",
    short: "Ready",
    title: "Ready for a first connected tool",
    range: "9–15",
    meaning: "Systems and habits are in place. AI just can't reach the work.",
  },
  {
    id: "agent-ready",
    short: "Agent-ready",
    title: "Agent-ready: scale it",
    range: "16–21",
    meaning:
      "Clean foundations. The gain now comes from connecting several systems and automating multi-step work.",
  },
];

const aiUse = (who) => ({
  area: "AI use",
  q: `How does the ${who} use AI today?`,
  options: four(
    "Paid team seats for everyone, with a written usage policy",
    "Some paid seats, but no shared policy or shared prompts",
    "Individuals use their own accounts",
    "Not at all, or it's banned",
  ),
});
const aiAccess = (systems, writing) => ({
  area: "AI access",
  q: "Can your AI see your systems?",
  options: four(
    `Yes. It's connected to at least one system (${systems}) and can read it`,
    "We export files or paste data in and out by hand",
    `It's only used for writing ${writing}`,
    "No",
  ),
});

const GENERAL = {
  id: "general",
  path: "/scorecard",
  label: "For any team",
  noun: "company",
  headline: "Is your team using AI, or just paying for it?",
  sub: "Seven questions, about sixty seconds. For any company where the same job gets done every week or month.",
  policyNote: "Start with a company-wide AI policy.",
  questions: [
    {
      area: "Systems",
      q: "Where does the work actually live?",
      options: four(
        "One main system everyone uses, plus a tool that tracks the work",
        "A main system, plus spreadsheets for tracking",
        "A mix of tools, spreadsheets and email folders",
        "Mostly email, paper or personal files",
      ),
    },
    {
      area: "Requests",
      q: "How do you collect what you need from customers or colleagues?",
      options: four(
        "A portal or form that reminds people automatically",
        "A portal or form, but someone still chases by hand",
        "Email and phone, tracked in a spreadsheet",
        "Email and phone, tracked in people's heads",
      ),
    },
    {
      area: "The repeated job",
      q: "How is your most repeated job run?",
      options: four(
        "A standard checklist, tracked in a tool, with owners and due dates",
        "A checklist exists, but it lives in a spreadsheet or doc",
        "Each person has their own way of doing it",
        "There's no set way; it gets done when it gets done",
      ),
    },
    aiUse("team"),
    aiAccess("your main system, an inbox", "emails and documents"),
    {
      area: "Security",
      q: "How are access and security handled?",
      options: four(
        "Two-step sign-in on every system, access by role, and a data policy that covers AI",
        "Two-step sign-in on most systems, with informal rules",
        "Some shared logins",
        "Shared passwords and no policy",
      ),
    },
    {
      area: "Consistency",
      q: "How consistent is the way work is set up?",
      options: four(
        "Standard templates, naming and written steps for most work",
        "Standard for new work, with older work all over the place",
        "Partly documented",
        "Every case is a one-off",
      ),
    },
  ],
  focus: {
    q: "What eats the most hours each month?",
    options: [
      { id: "chasing", label: "Chasing information" },
      { id: "copying", label: "Copying between systems" },
      { id: "checking", label: "Checking other people's work" },
      { id: "reporting", label: "Reporting" },
      { id: "onboarding", label: "Onboarding new people" },
    ],
  },
  results: [
    {
      where:
        "Your team is doing the work, but it lives in too many places: the main system, a spreadsheet, someone's inbox. AI can't help with work it can't find, and pointing it at messy data would only make mistakes faster. This is the most common starting point, and it can be fixed in a quarter.",
      moves: [
        "Pick one system of record for the work and one checklist format for your most repeated job.",
        "Turn on two-step sign-in everywhere and retire shared logins.",
        "Move requests out of email and into one tracked place.",
      ],
      plan: [
        "List every system and spreadsheet the team touches. Write the checklist for your most repeated job.",
        "Move tracking into one tool. Put a simple AI usage policy and paid seats in place.",
        "Re-take the scorecard. You should land in the next band, ready for a first tool.",
      ],
    },
    {
      where:
        "You have real systems and you already pay for AI, but it sits outside the work. People still copy between your main system, spreadsheets and email, so AI ends up as a writing assistant. One well-chosen tool could take hours off a job you do every month.",
      moves: [
        "Pick the single most repeated job.",
        "Map which systems it touches and who approves each step.",
        "Switch on the connections your software already offers for simple questions. Scope the cross-system piece separately.",
      ],
      plan: [
        "Map the job and measure the hours it takes today.",
        "Build one tool for that job. Try it with two people on real work.",
        "Roll it out to the team, measure the hours saved, and choose the second job.",
      ],
    },
    {
      where:
        "Your foundations are better than most: consistent setup, a tracked routine, paid AI and sound security. The limit now is that every job crosses several systems, and nobody's time goes to joining them up. This is where a tool built around your work earns its keep.",
      moves: [
        "Rank your jobs by hours times frequency, and start with the top two.",
        "Give your AI shared, company-level context instead of leaving it to each person's own chats.",
        "Put access review and a look at the log on a schedule, so the setup stays safe as it grows.",
      ],
      plan: [
        "Review how AI is used today and which connections you already have. Pick two jobs.",
        "Build both, moving from read-only to approved changes.",
        "Report the hours saved. Decide what to do with the time.",
      ],
    },
  ],
};

const ACCOUNTING = {
  id: "accounting",
  path: "/scorecard/accounting",
  category: "accounting-firms",
  label: "For accounting and bookkeeping firms",
  noun: "firm",
  headline: "Is your firm using AI, or just paying for it?",
  sub: "Seven questions, about sixty seconds. Built for accounting and bookkeeping firms with 5–40 people.",
  policyNote: "Start with a firm-wide AI policy.",
  questions: [
    {
      area: "Systems",
      q: "Where does client work actually live?",
      options: [
        o(
          "A",
          3,
          "One cloud ledger (QuickBooks Online or Xero) plus a practice-management tool everyone uses",
        ),
        o(
          "B",
          2,
          "A cloud ledger, plus spreadsheets for the checklists and tracking",
        ),
        o(
          "C",
          1,
          "A mix of cloud and desktop ledgers, spreadsheets and email folders",
        ),
        o("D", 0, "Mostly desktop software, paper or personal files"),
      ],
    },
    {
      area: "Client intake",
      q: "How do you collect documents and answers from clients?",
      options: [
        o("A", 3, "A portal or request tool that sends automatic reminders"),
        o("B", 2, "A portal, but staff still chase by hand"),
        o("C", 1, "Email and phone, tracked in a spreadsheet"),
        o("D", 0, "Email and phone, tracked in people's heads"),
      ],
    },
    {
      area: "Month-end close",
      q: "How is month-end close run?",
      options: [
        o(
          "A",
          3,
          "A standard checklist for each client, tracked in a tool, with clear owners and due dates",
        ),
        o("B", 2, "A checklist exists, but it lives in a spreadsheet or doc"),
        o("C", 1, "Each staffer has their own way of doing it"),
        o("D", 0, "There's no defined close, it gets done when it gets done"),
      ],
    },
    {
      area: "AI use",
      q: "How does the firm use ChatGPT or Claude today?",
      options: [
        o(
          "A",
          3,
          "Paid Team or Enterprise seats for everyone, with a written usage policy",
        ),
        o("B", 2, "Some paid seats, but no shared policy or shared prompts"),
        o("C", 1, "Individuals use their own accounts"),
        o("D", 0, "Not at all, or it's banned"),
      ],
    },
    {
      area: "AI access",
      q: "Can your AI see your systems?",
      options: [
        o(
          "A",
          3,
          "Yes. It's connected to at least one system (ledger, practice management, inbox) and can read it",
        ),
        o("B", 2, "We export files or paste data in and out by hand"),
        o("C", 1, "It's only used for writing emails and memos"),
        o("D", 0, "No"),
      ],
    },
    {
      area: "Security",
      q: "How are access and security handled?",
      options: [
        o(
          "A",
          3,
          "MFA on every system, role-based access, and a client-data policy that covers AI",
        ),
        o("B", 2, "MFA on most systems, with informal rules"),
        o("C", 1, "Some shared logins"),
        o("D", 0, "Shared passwords and no policy"),
      ],
    },
    {
      area: "Client setup",
      q: "How standardized is client setup?",
      options: [
        o(
          "A",
          3,
          "A standard chart of accounts, bank rules, naming conventions and SOPs for most clients",
        ),
        o(
          "B",
          2,
          "Standard for new clients, with older clients all over the place",
        ),
        o("C", 1, "Partly documented"),
        o("D", 0, "Every client is a one-off"),
      ],
    },
  ],
  focus: {
    q: "What eats the most hours each month?",
    options: [
      { id: "close", label: "Month-end close" },
      { id: "chasing", label: "Chasing client documents" },
      { id: "review", label: "Reviewing staff work" },
      { id: "clients", label: "Onboarding new clients" },
      { id: "staff", label: "Onboarding new staff" },
    ],
  },
  results: [
    {
      where:
        "Your team is doing the work, but it lives in too many places: the ledger, a spreadsheet, someone's inbox. AI can't help with work it can't find, and pointing it at messy data now would just make mistakes faster. This is the most common starting point, and it's fixable in a quarter.",
      moves: [
        "Pick one system of record for client work and one checklist format for close.",
        "Turn on MFA everywhere and retire shared logins.",
        "Move document requests out of email and into one tracked place.",
      ],
      plan: [
        "Inventory every system and spreadsheet the team touches. Write the close checklist for your top 10 clients.",
        "Move tracking into one tool. Put a simple AI usage policy and paid seats in place.",
        "Re-take the scorecard. You should land in the next band and be ready to connect the first tool.",
      ],
    },
    {
      where:
        "You've got real systems and you're already paying for AI, but it sits outside the work. Staff still copy data between {ledger}, spreadsheets and email, so ChatGPT or Claude ends up as a writing assistant. One well-chosen connection could take hours off every close.",
      moves: [
        "Pick the single most repetitive workflow, usually month-end close prep or document chasing.",
        "Map exactly which systems that workflow touches and who approves each step.",
        "Switch on the official connectors for single-client questions. Scope the cross-client piece separately.",
      ],
      plan: [
        "Map the workflow and measure the hours it takes today. Clean up client setup for the clients in scope.",
        "Ship one connected tool for that workflow. Pilot it with 2 staff on 10 clients.",
        "Roll it out firm-wide, measure the hours saved, and choose the second workflow.",
      ],
    },
    {
      where:
        "Your foundations are better than most firms your size: standard setups, tracked close, paid AI and decent security. The constraint now is that every workflow crosses several systems, and nobody's time is spent wiring them together. This is where connected agents compound.",
      moves: [
        "Rank your workflows by hours times frequency, and connect the top two.",
        "Give your AI shared, firm-level context (client setup, SOPs, close status) rather than leaving it to each person's own chats.",
        "Put monitoring and access review on a schedule so the setup stays safe as it grows.",
      ],
      plan: [
        "Audit current AI usage and the integrations you already have. Pick two workflows to connect.",
        "Connect both and move from read-only to approved write actions.",
        "Report hours saved per client. Use that capacity for more clients or advisory work.",
      ],
    },
  ],
};

const RECRUITING = {
  id: "recruiting",
  path: "/scorecard/recruiting",
  category: "recruiting-agencies",
  label: "For recruiting and staffing agencies",
  noun: "agency",
  headline: "Is your agency using AI, or just paying for it?",
  sub: "Seven questions, about sixty seconds. Built for recruiting and staffing agencies with 5–40 people.",
  policyNote: "Start with an agency-wide AI policy.",
  questions: [
    {
      area: "Systems",
      q: "Where does candidate and client work actually live?",
      options: four(
        "One ATS or CRM that every recruiter uses for candidates, roles and clients",
        "An ATS, plus spreadsheets for tracking and reporting",
        "A mix of ATS, spreadsheets, inboxes and personal files",
        "Mostly spreadsheets, email and personal files",
      ),
    },
    {
      area: "Client requirements",
      q: "How do you capture what a client wants for a role?",
      options: four(
        "A structured intake for every role, stored in the ATS",
        "A job-order document for every role, but it lives outside the ATS",
        "In email threads, found when needed",
        "In the recruiter's head",
      ),
    },
    {
      area: "Submissions",
      q: "How are candidate submissions prepared?",
      options: four(
        "A standard template and checklist, tracked in a tool",
        "A template exists, but each recruiter fills it in by hand",
        "Each recruiter has their own format",
        "No standard; it depends on the client and the day",
      ),
    },
    aiUse("agency"),
    aiAccess("ATS, CRM, inbox", "emails and job posts"),
    {
      area: "Security",
      q: "How are access and candidate data handled?",
      options: four(
        "MFA on every system, role-based access, and a candidate-data policy that covers AI",
        "MFA on most systems, with informal rules",
        "Some shared logins",
        "Shared passwords and no policy",
      ),
    },
    {
      area: "Reporting",
      q: "How do you report to clients and to your own leadership?",
      options: four(
        "Reports update automatically from the ATS",
        "Someone exports from the ATS and builds the report weekly",
        "A spreadsheet maintained by hand",
        "We answer when someone asks",
      ),
    },
  ],
  focus: {
    q: "What eats the most hours each week?",
    options: [
      { id: "submissions", label: "Preparing submissions" },
      { id: "reports", label: "Keeping client reports current" },
      { id: "feedback", label: "Chasing client feedback" },
      { id: "sourcing", label: "Sourcing and outreach" },
      { id: "onboarding", label: "Onboarding new recruiters" },
    ],
  },
  results: [
    {
      where:
        "Your recruiters are doing the work, but it lives in too many places: the ATS, a spreadsheet, someone's inbox. AI can't help with work it can't find. This is the most common starting point, and it can be fixed in a quarter.",
      moves: [
        "Pick one ATS as the system of record for candidates, roles and clients.",
        "Turn on MFA everywhere and retire shared logins.",
        "Move client requirements out of email and into a structured intake for every role.",
      ],
      plan: [
        "List every system and spreadsheet the desk touches. Write one submission template.",
        "Move tracking into the ATS. Put a simple AI usage policy and paid seats in place.",
        "Re-take the scorecard. You should land in the next band, ready for a first tool.",
      ],
    },
    {
      where:
        "You have a real ATS and you already pay for AI, but it sits outside the work. Recruiters still copy between the ATS, email and client reports, so AI ends up as a writing assistant. One well-chosen tool could take hours off every submission.",
      moves: [
        "Pick the most repeated job, usually submission packs or client reports.",
        "Map which systems it touches and who approves what goes to a client.",
        "Switch on your ATS's own connection for single-system questions. Scope the cross-system piece separately.",
      ],
      plan: [
        "Map the job and measure the hours it takes today.",
        "Build one tool for that job. Try it with two recruiters on live roles.",
        "Roll it out to the desk, measure the hours saved, and choose the second job.",
      ],
    },
    {
      where:
        "Your foundations are better than most agencies your size: one ATS, standard submissions, paid AI and sound security. The limit now is that every job crosses the ATS, email and client reporting, and nobody's time goes to joining them up.",
      moves: [
        "Rank your jobs by hours times frequency, and start with the top two.",
        "Give your AI shared agency-level context: client requirements, submission templates, pipeline status.",
        "Keep screening and ranking decisions with recruiters, and review access on a schedule.",
      ],
      plan: [
        "Review how AI is used today and what your ATS already connects to. Pick two jobs.",
        "Build both, moving from read-only to approved sends.",
        "Report hours saved per recruiter. Use the time for more roles or better client contact.",
      ],
    },
  ],
};

const PROPERTY = {
  id: "property",
  path: "/scorecard/property",
  category: "property-management",
  label: "For property managers",
  noun: "company",
  headline: "Is your team using AI, or just paying for it?",
  sub: "Seven questions, about sixty seconds. Built for property management companies with 5–40 people.",
  policyNote: "Start with a company-wide AI policy.",
  questions: [
    {
      area: "Systems",
      q: "Where does the portfolio actually live?",
      options: four(
        "One management system everyone uses for units, leases and owners",
        "A management system, plus spreadsheets for tracking",
        "A mix of systems, spreadsheets and inboxes",
        "Mostly spreadsheets, email and paper",
      ),
    },
    {
      area: "Maintenance",
      q: "How does a maintenance request get from tenant to done?",
      options: four(
        "A tenant portal creates the work order, assigns it and updates the tenant automatically",
        "A portal takes the request, but staff assign and follow up by hand",
        "Phone and email, tracked in a spreadsheet",
        "Phone and email, tracked in people's heads",
      ),
    },
    {
      area: "Owner reporting",
      q: "How are owner statements and reports prepared?",
      options: four(
        "They come out of the system on a schedule, with little manual work",
        "Exported from the system, then finished by hand each month",
        "Built in spreadsheets each month",
        "Sent when an owner asks",
      ),
    },
    aiUse("team"),
    aiAccess("management system, inbox, phone log", "emails and listings"),
    {
      area: "Security",
      q: "How are access and tenant data handled?",
      options: four(
        "Two-step sign-in on every system, access by role, and a tenant-data policy that covers AI",
        "Two-step sign-in on most systems, with informal rules",
        "Some shared logins",
        "Shared passwords and no policy",
      ),
    },
    {
      area: "Leases",
      q: "How are renewals and move-ins handled?",
      options: four(
        "A standard checklist for each, tracked in a tool, with owners and dates",
        "A checklist exists, but it lives in a spreadsheet or doc",
        "Each manager has their own way",
        "Handled when someone notices the date",
      ),
    },
  ],
  focus: {
    q: "What eats the most hours each month?",
    options: [
      { id: "maintenance", label: "Maintenance coordination" },
      { id: "owners", label: "Owner statements and questions" },
      { id: "tenants", label: "Tenant messages" },
      { id: "renewals", label: "Lease renewals" },
      { id: "onboarding", label: "Onboarding new properties" },
    ],
  },
  results: [
    {
      where:
        "Your team is doing the work, but it lives in too many places: the management system, a spreadsheet, someone's inbox, a phone log. AI can't help with work it can't find. This is the most common starting point, and it can be fixed in a quarter.",
      moves: [
        "Pick one system of record for units, leases and owners.",
        "Turn on two-step sign-in everywhere and retire shared logins.",
        "Move maintenance requests out of phone and email into one tracked place.",
      ],
      plan: [
        "List every system and spreadsheet the team touches. Write the checklist for a move-in and a renewal.",
        "Move tracking into one tool. Put a simple AI usage policy and paid seats in place.",
        "Re-take the scorecard. You should land in the next band, ready for a first tool.",
      ],
    },
    {
      where:
        "You have a real management system and you already pay for AI, but it sits outside the work. Staff still copy between the system, email and owner reports, so AI ends up as a writing assistant. One well-chosen tool could take hours off every month.",
      moves: [
        "Pick the most repeated job, usually maintenance follow-up or owner statements.",
        "Map which systems it touches and who approves what goes to an owner or a tenant.",
        "Switch on the connections your software already offers for simple questions. Scope the cross-system piece separately.",
      ],
      plan: [
        "Map the job and measure the hours it takes today.",
        "Build one tool for that job. Try it on part of the portfolio.",
        "Roll it out across the portfolio, measure the hours saved, and choose the second job.",
      ],
    },
    {
      where:
        "Your foundations are better than most managers your size: one system, standard checklists, paid AI and sound security. The limit now is that every job crosses the management system, email and owner reporting, and nobody's time goes to joining them up.",
      moves: [
        "Rank your jobs by hours times frequency, and start with the top two.",
        "Give your AI shared context: properties, owners' preferences, vendor lists and your house rules.",
        "Keep applicant screening decisions with people, and review access on a schedule.",
      ],
      plan: [
        "Review how AI is used today and what your management system already connects to. Pick two jobs.",
        "Build both, moving from read-only to approved changes.",
        "Report hours saved per manager. Use the time for more doors or better owner contact.",
      ],
    },
  ],
};

const AGENCIES = {
  id: "agencies",
  path: "/scorecard/agencies",
  category: "agencies-consultancies",
  label: "For agencies and consultancies",
  noun: "team",
  headline: "Is your team using AI, or just paying for it?",
  sub: "Seven questions, about sixty seconds. Built for agencies and consultancies with 5–40 people.",
  policyNote: "Start with a company-wide AI policy.",
  questions: [
    {
      area: "Systems",
      q: "Where does client and project work actually live?",
      options: four(
        "One CRM and one project tool that everyone uses",
        "A CRM and a project tool, plus spreadsheets for tracking",
        "A mix of tools, spreadsheets and inboxes",
        "Mostly email, documents and personal files",
      ),
    },
    {
      area: "Inquiries",
      q: "What happens when a new inquiry arrives?",
      options: four(
        "It lands in the CRM with the client's history, and someone is assigned automatically",
        "It lands in the CRM, but someone gathers the context by hand",
        "It sits in an inbox until someone picks it up",
        "It depends on who saw it first",
      ),
    },
    {
      area: "Proposals",
      q: "How are proposals prepared?",
      options: four(
        "From standard templates and a rate card, with a set approval step",
        "Templates exist, but each proposal is rebuilt by hand",
        "Each person writes their own",
        "From scratch every time",
      ),
    },
    aiUse("team"),
    aiAccess("CRM, project tool, inbox", "emails and proposals"),
    {
      area: "Security",
      q: "How are access and client data handled?",
      options: four(
        "Two-step sign-in on every system, access by role, and a client-data policy that covers AI",
        "Two-step sign-in on most systems, with informal rules",
        "Some shared logins",
        "Shared passwords and no policy",
      ),
    },
    {
      area: "Handover",
      q: "How does won work get handed to delivery?",
      options: four(
        "The project record, brief and commitments are created from the proposal automatically",
        "A handover checklist exists, filled in by hand",
        "A meeting and a forwarded email thread",
        "Delivery finds out when the client asks",
      ),
    },
  ],
  focus: {
    q: "What eats the most hours each month?",
    options: [
      { id: "proposals", label: "Writing proposals" },
      { id: "context", label: "Gathering client context" },
      { id: "handover", label: "Handing work to delivery" },
      { id: "reports", label: "Status reports for clients" },
      { id: "onboarding", label: "Onboarding new people" },
    ],
  },
  results: [
    {
      where:
        "Your team is doing the work, but it lives in too many places: the CRM, a spreadsheet, someone's inbox, a shared drive. AI can't help with work it can't find. This is the most common starting point, and it can be fixed in a quarter.",
      moves: [
        "Pick one place for client records and one for projects, and use them for everything.",
        "Turn on two-step sign-in everywhere and retire shared logins.",
        "Write one proposal template and one rate card that everyone uses.",
      ],
      plan: [
        "List every system and spreadsheet the team touches. Write the handover checklist.",
        "Move tracking into your CRM and project tool. Put a simple AI usage policy and paid seats in place.",
        "Re-take the scorecard. You should land in the next band, ready for a first tool.",
      ],
    },
    {
      where:
        "You have a real CRM and project tool and you already pay for AI, but it sits outside the work. People still copy between the inbox, the CRM, the proposal and the project plan, so AI ends up as a writing assistant. One well-chosen tool could take hours off every proposal and handover.",
      moves: [
        "Pick the most repeated job, usually proposals or the handover to delivery.",
        "Map which systems it touches and who approves scope and price.",
        "Switch on the connections your software already offers for simple questions. Scope the cross-system piece separately.",
      ],
      plan: [
        "Map the job and measure the hours it takes today.",
        "Build one tool for that job. Try it on the next five inquiries.",
        "Roll it out to the team, measure the hours saved, and choose the second job.",
      ],
    },
    {
      where:
        "Your foundations are better than most teams your size: one CRM, standard proposals, paid AI and sound security. The limit now is that every job crosses the inbox, the CRM and the project tool, and nobody's time goes to joining them up.",
      moves: [
        "Rank your jobs by hours times frequency, and start with the top two.",
        "Give your AI shared context: your services, rate card, past proposals and how you run a project.",
        "Keep approval of scope, price and client commitments with people, and review access on a schedule.",
      ],
      plan: [
        "Review how AI is used today and what your tools already connect to. Pick two jobs.",
        "Build both, moving from read-only to approved sends.",
        "Report hours saved per proposal and per handover. Use the time for client work.",
      ],
    },
  ],
};

export const SCORECARDS = {
  general: GENERAL,
  accounting: ACCOUNTING,
  recruiting: RECRUITING,
  property: PROPERTY,
  agencies: AGENCIES,
};

// "/scorecard" is the general one; "/scorecard/{id}" is an audience's own.
export const getScorecard = (id) =>
  id === undefined ? GENERAL : (SCORECARDS[id] ?? null);

// The scorecard that belongs to a category page, if it has one.
export const scorecardForCategory = (slug) =>
  Object.values(SCORECARDS).find((c) => c.category === slug) ?? null;

// Kept for callers that only need the accounting set.
export const QUESTIONS = ACCOUNTING.questions;
export const FOCUS = ACCOUNTING.focus;

export const total = (answers) => answers.reduce((s, v) => s + v, 0);

// Security is a gate: shared passwords and no policy cap the band at Scattered.
export function bandIndex(answers) {
  const t = total(answers);
  const index = t <= 8 ? 0 : t <= 15 ? 1 : 2;
  return answers[SECURITY] === 0 ? 0 : index;
}

export function notes(answers, card = ACCOUNTING) {
  const out = [];
  if (answers[SECURITY] === 0 && total(answers) > 8)
    out.push(
      "Security comes first. Shared passwords and no policy cap your band until they are fixed, whatever the total.",
    );
  if (answers[AI_USE] === 0) out.push(card.policyNote);
  return out;
}

export function where(answers, card = ACCOUNTING) {
  const ledger = answers[0] >= 2 ? "your cloud ledger" : "your ledger";
  return card.results[bandIndex(answers)].where.replace("{ledger}", ledger);
}

export function focusLine(answers, focusId, card = ACCOUNTING) {
  const item = card.focus.options.find((f) => f.id === focusId);
  if (!item) return "";
  const label = item.label.toLowerCase();
  return bandIndex(answers) === 0
    ? `You said ${label} eats the most hours. Fixing the foundations above is what makes that job something an agent can safely take on.`
    : `You said ${label} eats the most hours, so that is where we would start.`;
}

// The two lowest-scoring areas, only when they are genuinely weak (0 or 1 point).
export function gaps(answers, card = ACCOUNTING) {
  return answers
    .map((points, i) => ({ points, area: card.questions[i].area, i }))
    .filter((x) => x.points <= 1)
    .sort((a, b) => a.points - b.points || a.i - b.i)
    .slice(0, 2);
}

// Answers travel in the URL as seven digits (points per question), e.g. 3210232,
// so a result can be reloaded or shared and the contact page can reference it.
export const encode = (answers) => answers.join("");
export function decode(value) {
  if (typeof value !== "string" || !/^[0-3]{7}$/.test(value)) return null;
  return [...value].map(Number);
}
export function letterOf(answers) {
  return answers.map((p) => "DCBA"[p]).join("");
}
